import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { type InferType, object, string } from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useSendSupportTicketMutation } from '@/generated';
import { useToast } from '@/utils/useToast';

const initialValues = {
  description: '',
};

const needHelpFormSchema = object({
  description: string().required('Required Field'),
});

type NeedHelpFormType = InferType<typeof needHelpFormSchema>;

type NeedHelpFormProps = {
  onClose: () => void;
};

const NeedHelpForm = ({ onClose }: NeedHelpFormProps) => {
  const {
    formState: { errors, isValid },
    register,
    handleSubmit,
    reset: resetForm,
  } = useForm<NeedHelpFormType>({
    resolver: yupResolver(needHelpFormSchema),
    defaultValues: initialValues,
  });
  const [sendSupportTicket, { loading: isLoadingSendSupportTicket }] =
    useSendSupportTicketMutation();
  const toast = useToast();

  const [showAlert, setShowAlert] = useState(false);

  const onSubmit = ({ description }: NeedHelpFormType) => {
    sendSupportTicket({
      variables: {
        message: description,
        contextUrl: window.location.href,
      },
      onCompleted() {
        setShowAlert(true);
        resetForm();
      },
      onError(e) {
        toast(e.message, 'error');
      },
    });
  };

  const handleInputFocus = () => {
    if (!showAlert) return;

    setShowAlert(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction="column"
          sx={{
            gap: 2,
            p: 3,
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Need Help?</Typography>
            <IconButton aria-label="close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <TextField
            {...register('description')}
            multiline
            placeholder="Describe what happened..."
            label="How can we help?"
            rows={4}
            helperText={errors.description?.message}
            error={!!errors.description}
            onFocus={handleInputFocus}
          />
          <Stack
            direction="row"
            sx={{
              justifyContent: 'end',
              gap: 2,
            }}
          >
            {isLoadingSendSupportTicket && <CircularProgress size={36} />}

            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || isLoadingSendSupportTicket}
            >
              Send
            </Button>
          </Stack>

          {showAlert && (
            <Alert severity="success">
              Your message has been submitted to the Technical Support team, and
              someone will reply to you via email shortly.
            </Alert>
          )}
        </Stack>
      </form>
    </>
  );
};

export default NeedHelpForm;
